import { Component } from '@theme/component';

export class HeroComponent extends Component {
  #slides = [];
  #railItems = [];
  #activeIndex = 0;
  #delay = 5000;
  #timer = null;
  #condenseTimer = null;
  #injected = new WeakSet();
  #touchX = 0;
  #touchY = 0;
  #touchDX = 0;
  #touchDY = 0;
  #touchOn = false;
  #reducedMotion = false;
  #idleTimeout = null;
  #idleDelay = 3000;
  #resumeTimer = null;
  #resumeDelay = 15000;
  #enterAnim = null;
  #leaveAnim = null;
  #animSlide = null;
  #animPrev = null;
  #visibilityObserver = null;
  #isVisible = true;
  #fillEl = null;

  #onPrevClick = null;
  #onNextClick = null;

  /** @type {Array<{item: Element, handler: Function}>} */
  #onRailClicks = null;

  #touchViewport = null;
  #onTouchStart = null;
  #onTouchMove = null;
  #onTouchEnd = null;

  connectedCallback() {
    super.connectedCallback();
    this.#slides = Array.from(this.querySelectorAll('[data-hero-slide]'));

    const rail = this.querySelector('.hero-section__rail');
    this.#railItems = rail
      ? Array.from(rail.querySelectorAll('.hero-section__rail-item'))
      : [];
    this.#delay =
      parseInt(this.dataset.heroAutoplayDelay || '5000', 10) || 5000;
    this.#reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (this.#slides[0]) this.#injected.add(this.#slides[0]);
    this.addEventListener('loadeddata', this.#onLoadedData, true);

    // Single-slide heroes have no autoplay/advance machinery (there is
    // nothing to cycle between) and no arrows/rail, but the current
    // slide's video must still start on the same playback path: play in a
    // no-preference context, stay paused under reduced motion. We reuse
    // #playActiveVideo so playback behavior stays in parity with the
    // multi-slide path instead of duplicating the logic here.
    if (this.#slides.length === 1) {
      this.#bindVisibilityObserver();
      this.#playActiveVideo();
      return;
    }
    if (this.#slides.length < 2) return;

    this.#bindControls();
    this.#bindKeyboard();
    this.#bindTouch();
    this.#bindIdleDetection();
    this.#bindVisibilityObserver();
    this.#scheduleCondense();
    this.#startTimer();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cancelAdvance();
    clearTimeout(this.#condenseTimer);
    clearTimeout(this.#resumeTimer);
    clearTimeout(this.#idleTimeout);
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
    this.removeEventListener('loadeddata', this.#onLoadedData, true);
    document.removeEventListener('keydown', this._onKeyDown);

    const prevBtn = this.querySelector('[data-hero-prev]');
    const nextBtn = this.querySelector('[data-hero-next]');
    if (prevBtn && this.#onPrevClick) {
      prevBtn.removeEventListener('click', this.#onPrevClick);
      this.#onPrevClick = null;
    }
    if (nextBtn && this.#onNextClick) {
      nextBtn.removeEventListener('click', this.#onNextClick);
      this.#onNextClick = null;
    }
    if (this.#onRailClicks) {
      this.#onRailClicks.forEach(({ item, handler }) => {
        item.removeEventListener('click', handler);
      });
      this.#onRailClicks = null;
    }

    if (this.#touchViewport && this.#onTouchStart) {
      this.#touchViewport.removeEventListener('touchstart', this.#onTouchStart);
      this.#touchViewport.removeEventListener('touchmove', this.#onTouchMove);
      this.#touchViewport.removeEventListener('touchend', this.#onTouchEnd);
      this.#touchViewport = null;
      this.#onTouchStart = null;
      this.#onTouchMove = null;
      this.#onTouchEnd = null;
    }
  }

  /* ---------- media loading ---------- */

  #onLoadedData = (e) => {
    if (e.target.tagName === 'VIDEO')
      e.target.classList.remove('is-loading');
  };

  #injectMedia(slide) {
    if (this.#injected.has(slide)) return;
    this.#injected.add(slide);
    const tpl = slide.querySelector('template[data-hero-media-template]');
    if (!tpl) return;
    const container = tpl.parentNode;
    container.appendChild(tpl.content.cloneNode(true));
    tpl.remove();
  }

  #activeVideo() {
    const slide = this.#slides[this.#activeIndex];
    const videos = slide.querySelectorAll('video');
    for (let i = 0; i < videos.length; i++) {
      if (videos[i].getClientRects().length > 0) return videos[i];
    }
    return null;
  }

  #activeSlideHasVideo() {
    const slide = this.#slides[this.#activeIndex];
    return slide && slide.querySelector('video') != null;
  }

  #playActiveVideo() {
    // Start the active slide's video. Respects prefers-reduced-motion by
    // leaving the video paused. Shared by the single-slide init path and
    // the multi-slide timer so both behave identically.
    if (this.#reducedMotion) return;
    const v = this.#activeVideo();
    if (!v) return;
    v.currentTime = 0;
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
    v.addEventListener('timeupdate', this.#onVideoTimeupdate);
    v.addEventListener('ended', this.#onVideoEnded);
  }

  /* ---------- progress ---------- */

  #cancelAdvance() {
    if (this.#timer) {
      clearTimeout(this.#timer);
      this.#timer = null;
    }
    if (this.#enterAnim) {
      this.#enterAnim.cancel();
      this.#enterAnim = null;
    }
    if (this.#leaveAnim) {
      this.#leaveAnim.cancel();
      this.#leaveAnim = null;
    }
    this.#stopProgressAnimation();
    this.#detachVideoProgress();
  }

  #stopProgressAnimation() {
    if (this.#fillEl) {
      this.#fillEl.style.animation = '';
      this.#fillEl = null;
    }
    for (let i = 0; i < this.#railItems.length; i++) {
      const fill = this.#railItems[i].querySelector(
        '.hero-section__rail-fill'
      );
      if (fill) fill.style.animation = '';
      this.#railItems[i].style.removeProperty('--rail-progress');
    }
  }

  #detachVideoProgress() {
    const v = this.#activeVideo();
    if (!v) return;
    v.removeEventListener('timeupdate', this.#onVideoTimeupdate);
    v.removeEventListener('ended', this.#onVideoEnded);
  }

  #onVideoTimeupdate = () => {
    const v = this.#activeVideo();
    if (!v || !Number.isFinite(v.duration) || v.duration <= 0) return;
    const pct = Math.min(v.currentTime / v.duration, 1);
    const item = this.#railItems[this.#activeIndex];
    if (item) item.style.setProperty('--rail-progress', String(pct));
  };

  #onVideoEnded = () => {
    this.#detachVideoProgress();
    this.#goTo(this.#activeIndex + 1, false, 'forward');
  };

  #startTimer() {
    // Only clean up timer and progress — never cancel the slide
    // transition animations that #goTo just created. #cancelAdvance()
    // is only called inside #goTo (to stop the PREVIOUS slide's
    // timers) and disconnectedCallback.
    if (this.#timer) {
      clearTimeout(this.#timer);
      this.#timer = null;
    }
    this.#stopProgressAnimation();
    this.#detachVideoProgress();

    if (this.#reducedMotion) return;

    const v = this.#activeVideo();
    if (v) {
      this.#playActiveVideo();
    } else {
      this.#startCSSProgress();
    }

    // Single setTimeout for reliable slide timing — no RAF loop.
    // CSS animation handles the visual progress bar (GPU-driven, zero
    // main-thread overhead). On mobile where the fill is display:none,
    // the CSS animation is skipped but the timeout still advances.
    this.#timer = setTimeout(() => {
      this.#timer = null;
      this.#goTo(this.#activeIndex + 1, false, 'forward');
    }, this.#delay);
  }

  #startCSSProgress() {
    const activeRail = this.#railItems[this.#activeIndex];
    if (!activeRail) return;
    const fill = activeRail.querySelector('.hero-section__rail-fill');
    if (!fill) return;

    // On mobile (<600px) the fill is display:none — CSS animations
    // won't run. Skip the visual animation; the setTimeout in
    // #startTimer still handles slide timing.
    if (getComputedStyle(fill).display === 'none') return;

    this.#fillEl = fill;

    fill.style.animation = 'none';
    void fill.offsetHeight;
    fill.style.animation =
      'hero-rail-fill ' + this.#delay + 'ms linear forwards';

    if (!this.#isVisible) {
      fill.style.animationPlayState = 'paused';
    }
  }

  /* ---------- resume after user interaction ---------- */

  #scheduleResume() {
    clearTimeout(this.#resumeTimer);
    this.#stopProgressAnimation();
    this.#resumeTimer = setTimeout(() => {
      this.#startTimer();
    }, this.#resumeDelay);
  }

  #scheduleCondense() {
    clearTimeout(this.#condenseTimer);
    const rail = this.querySelector('.hero-section__rail');
    if (rail) rail.dataset.railCondensed = 'false';
    this.#condenseTimer = setTimeout(() => {
      if (rail) rail.dataset.railCondensed = 'true';
    }, 5000);
  }

  /* ---------- visibility observer ---------- */

  #bindVisibilityObserver() {
    if (!('IntersectionObserver' in window)) return;
    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          this.#onVisible();
        } else {
          this.#onHidden();
        }
      },
      { threshold: 0 }
    );
    this.#visibilityObserver.observe(this);
  }

  #onHidden() {
    this.#isVisible = false;
    // Pause CSS progress animation (visual only — setTimeout keeps
    // running so slide timing is unaffected)
    if (this.#fillEl) {
      this.#fillEl.style.animationPlayState = 'paused';
    }
    const v = this.#activeVideo();
    if (v && !v.paused) v.pause();
  }

  #onVisible() {
    this.#isVisible = true;
    if (this.#fillEl) {
      this.#fillEl.style.animationPlayState = 'running';
    }
    const v = this.#activeVideo();
    if (v && v.paused && v.currentTime > 0) {
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }
  }

  /* ---------- controls ---------- */

  #bindControls() {
    const prevBtn = this.querySelector('[data-hero-prev]');
    const nextBtn = this.querySelector('[data-hero-next]');
    if (prevBtn) {
      this.#onPrevClick = () => {
        this.#goTo(this.#activeIndex - 1, true, 'backward');
        prevBtn.blur();
      };
      prevBtn.addEventListener('click', this.#onPrevClick);
    }
    if (nextBtn) {
      this.#onNextClick = () => {
        this.#goTo(this.#activeIndex + 1, true, 'forward');
        nextBtn.blur();
      };
      nextBtn.addEventListener('click', this.#onNextClick);
    }
    this.#onRailClicks = [];
    this.#railItems.forEach((item, i) => {
      const handler = () => {
        this.#goTo(i, true);
        item.blur();
      };
      item.addEventListener('click', handler);
      this.#onRailClicks.push({ item, handler });
    });
  }

  #goTo(next, userInitiated = false, direction) {
    const len = this.#slides.length;
    next = ((next % len) + len) % len;
    if (
      next === this.#activeIndex &&
      this.#fillEl === null &&
      this.#timer === null
    ) {
      if (userInitiated) {
        if (this.#activeSlideHasVideo()) {
          this.#startTimer();
        } else {
          this.#scheduleResume();
        }
      } else {
        this.#startTimer();
      }
      return;
    }
    if (next === this.#activeIndex) return;
    this.#cancelAdvance();
    this.#resetIdleTimer();

    const prevIndex = this.#activeIndex;
    const prev = this.#slides[prevIndex];
    this.#activeIndex = next;
    const slide = this.#slides[this.#activeIndex];
    // Inject before probing for video: non-first slide media lives inside
    // <template data-hero-media-template>, whose contents are only
    // queryable once cloned into the DOM. Probing beforehand would always
    // see no video and wrongly park the slide in #scheduleResume.
    this.#injectMedia(slide);
    const targetHasVideo = slide && slide.querySelector('video') != null;

    // Two-slide carousels are degenerate: index 0 and 1 are adjacent in both
    // directions, so the wrap cases would invert rail-click slide-in direction.
    // Keep wrap direction hints only when there are at least three slides.
    const wrapForward = prevIndex === len - 1 && next === 0 && len > 2;
    const wrapBackward = prevIndex === 0 && next === len - 1 && len > 2;
    const forward =
      direction === 'forward'
        ? true
        : direction === 'backward'
          ? false
          : wrapForward
            ? true
            : wrapBackward
              ? false
              : next > prevIndex;

    if (this.#enterAnim) {
      this.#enterAnim.cancel();
      this.#enterAnim = null;
      if (this.#animSlide) {
        this.#animSlide.classList.remove('is-active');
        this.#animSlide.style.opacity = '';
        this.#animSlide.style.transform = '';
        this.#animSlide.style.transition = '';
        this.#animSlide.style.zIndex = '';
        this.#animSlide.style.pointerEvents = '';
        this.#animSlide.style.visibility = '';
        this.#animSlide.setAttribute('hidden', '');
        this.#animSlide.setAttribute('aria-hidden', 'true');
        this.#animSlide = null;
      }
    }
    if (this.#leaveAnim) {
      this.#leaveAnim.cancel();
      this.#leaveAnim = null;
      if (this.#animPrev) {
        this.#animPrev.classList.remove('is-active');
        this.#animPrev.style.opacity = '';
        this.#animPrev.style.transform = '';
        this.#animPrev.style.transition = '';
        this.#animPrev.style.zIndex = '';
        this.#animPrev.style.pointerEvents = '';
        this.#animPrev.style.visibility = '';
        this.#animPrev.setAttribute('hidden', '');
        this.#animPrev.setAttribute('aria-hidden', 'true');
        this.#animPrev = null;
      }
    }

    const offScreen = forward ? '100%' : '-100%';
    const leaveEnd = forward ? '-100%' : '100%';
    const easing = 'cubic-bezier(0.2, 0, 0, 1)';
    const duration = this.#reducedMotion ? 0 : 800;

    slide.style.transition = 'none';
    slide.style.opacity = '1';
    slide.style.zIndex = '2';
    slide.style.pointerEvents = 'auto';
    slide.style.visibility = 'visible';
    slide.removeAttribute('hidden');
    slide.setAttribute('aria-hidden', 'false');
    slide.classList.add('is-active');

    prev.style.transition = 'none';
    prev.style.zIndex = '1';
    prev.style.pointerEvents = 'none';
    prev.style.visibility = 'visible';

    const canAnimate = typeof slide.animate === 'function';

    if (canAnimate) {
      const enterAnim = slide.animate(
        [
          { transform: 'translateX(' + offScreen + ')' },
          { transform: 'translateX(0)' },
        ],
        { duration, easing, fill: 'forwards' }
      );

      const leaveAnim = prev.animate(
        [
          { transform: 'translateX(0)' },
          { transform: 'translateX(' + leaveEnd + ')' },
        ],
        { duration, easing, fill: 'forwards' }
      );

      this.#enterAnim = enterAnim;
      this.#leaveAnim = leaveAnim;
      this.#animSlide = slide;
      this.#animPrev = prev;

      const cleanup = () => {
        if (this.#enterAnim !== enterAnim) return;

        enterAnim.cancel();
        leaveAnim.cancel();
        this.#enterAnim = null;
        this.#leaveAnim = null;
        this.#animSlide = null;
        this.#animPrev = null;

        slide.style.opacity = '';
        slide.style.transform = '';
        slide.style.transition = '';

        prev.classList.remove('is-active');
        prev.style.opacity = '';
        prev.style.zIndex = '';
        prev.style.pointerEvents = '';
        prev.style.visibility = '';
        prev.style.transform = '';
        prev.style.transition = '';
        prev.setAttribute('hidden', '');
        prev.setAttribute('aria-hidden', 'true');
        prev.querySelectorAll('video').forEach((v) => v.pause());
      };

      const animDone = (anim) => {
        if (anim.finished) return anim.finished.catch(() => {});
        return new Promise((r) => { anim.onfinish = r; });
      };
      Promise.all([animDone(enterAnim), animDone(leaveAnim)]).then(cleanup);
    } else {
      prev.classList.remove('is-active');
      prev.setAttribute('hidden', '');
      prev.setAttribute('aria-hidden', 'true');
      prev.querySelectorAll('video').forEach((v) => v.pause());
    }

    for (let i = 0; i < this.#railItems.length; i++) {
      this.#railItems[i].classList.toggle(
        'is-active',
        i === this.#activeIndex
      );
    }

    const tone = slide.dataset.heroTone || 'dark';
    this.dataset.heroTone = tone;
    this.dispatchEvent(
      new CustomEvent('hero:tone-change', {
        detail: { tone },
        bubbles: true,
      })
    );

    this.#scheduleCondense();

    if (userInitiated) {
      if (targetHasVideo) {
        this.#startTimer();
      } else {
        this.#scheduleResume();
      }
    } else {
      this.#startTimer();
    }
  }

  /* ---------- keyboard ---------- */

  #bindKeyboard() {
    this._onKeyDown = (e) => {
      if (e.key === 'ArrowLeft')
        this.#goTo(this.#activeIndex - 1, true, 'backward');
      if (e.key === 'ArrowRight')
        this.#goTo(this.#activeIndex + 1, true, 'forward');
    };
    document.addEventListener('keydown', this._onKeyDown);
  }

  /* ---------- touch swipe ---------- */

  #bindTouch() {
    const viewport = this.querySelector('.hero-section__viewport');
    if (!viewport) return;

    this.#touchViewport = viewport;

    this.#onTouchStart = (e) => {
      if (e.target.closest('a, button, input')) return;
      this.#touchX = e.touches[0].clientX;
      this.#touchY = e.touches[0].clientY;
      this.#touchDX = 0;
      this.#touchDY = 0;
      this.#touchOn = true;
    };
    this.#onTouchMove = (e) => {
      if (!this.#touchOn) return;
      this.#touchDX = e.touches[0].clientX - this.#touchX;
      this.#touchDY = e.touches[0].clientY - this.#touchY;
      if (Math.abs(this.#touchDX) > Math.abs(this.#touchDY) * 1.15)
        e.preventDefault();
    };
    this.#onTouchEnd = () => {
      if (!this.#touchOn) return;
      this.#touchOn = false;
      if (
        Math.abs(this.#touchDX) > 56 &&
        Math.abs(this.#touchDX) > Math.abs(this.#touchDY) * 1.15
      ) {
        this.#goTo(
          this.#activeIndex + (this.#touchDX < 0 ? 1 : -1),
          true,
          this.#touchDX < 0 ? 'forward' : 'backward'
        );
      }
    };

    viewport.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    viewport.addEventListener('touchmove', this.#onTouchMove, { passive: false });
    viewport.addEventListener('touchend', this.#onTouchEnd);
  }

  /* ---------- idle detection ---------- */

  #resetIdleTimer() {
    this.removeAttribute('data-idle');
    clearTimeout(this.#idleTimeout);
    this.#idleTimeout = setTimeout(() => {
      if (this.#activeVideo()) {
        this.setAttribute('data-idle', '');
      }
    }, this.#idleDelay);
  }

  #bindIdleDetection() {
    this.#resetIdleTimer();
    this.addEventListener('mousemove', () => this.#resetIdleTimer());
    this.addEventListener('mouseleave', () => {
      clearTimeout(this.#idleTimeout);
      this.removeAttribute('data-idle');
    });
  }
}

if (!customElements.get('hero-component')) {
  customElements.define('hero-component', HeroComponent);
}
